/**
 * Settings > General: change the password of the signed-in user.
 * The server checks the current password, signs out the other sessions and
 * gives this one fresh cookies (POST /api/auth/change-password).
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { changePassword } from '@/api/endpoints/password';
import { ApiErrorException } from '@/api/client';
import { newPasswordError, passwordApiErrorKey } from '@/lib/passwordRules';
import { SettingsCard, SettingsRow } from './SettingsPrimitives';

export function ChangePasswordCard() {
  const { t } = useTranslation(['settings', 'admin', 'common']);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setOpen(false);
    setCurrent('');
    setNext('');
    setConfirmation('');
    setError('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const localError = newPasswordError(next, confirmation);
    if (localError) {
      setError(t(localError));
      return;
    }
    setError('');
    setSaving(true);
    try {
      await changePassword(current, next);
      toast.success(t('settings:password.saved'));
      reset();
    } catch (err) {
      const apiErr = err instanceof ApiErrorException ? err : null;
      setError(t(passwordApiErrorKey(apiErr?.code, apiErr?.status)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard>
      <SettingsRow label={t('settings:password.label')} last={!open}>
        {!open && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {t('settings:password.change')}
          </Button>
        )}
      </SettingsRow>
      {open && (
        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder={t('settings:password.current')} autoComplete="current-password" required />
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={t('settings:password.new')} autoComplete="new-password" required />
          <Input type="password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} placeholder={t('settings:password.confirm')} autoComplete="new-password" required />
          <p className="text-xs text-muted-foreground">{t('admin:passwordReset.hint')}</p>
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={reset}>{t('settings:password.cancel')}</Button>
            <Button type="submit" size="sm" disabled={saving || !current || !next || !confirmation}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('settings:password.save')}
            </Button>
          </div>
        </form>
      )}
    </SettingsCard>
  );
}
